import * as React from "react";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { Tooltip } from "@mui/material";
import {
  Navigate,
  matchPath,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { MaterialSymbol } from "@datalake/towercontrol-banner";
import getUserProfiles from "../tokenUtils";

const USERS = [
  "vic-qa-admin",
  "vic-qa-user",
  "vic-prod-admin",
  "vic-prod-user",
];
const ADMINS = ["vic-qa-admin", "vic-prod-admin"];

// Sidebar navigation, grouped by functional domain. A section has `children`;
// a top-level entry does not. `icon` is a Google Material Symbols Rounded
// name, `profiles` the Cognito groups allowed to see the entry. The Overview
// page (Victoria.js) renders its cards from the same sections.
export const MENU_SECTIONS = [
  {
    title: "Overview",
    url: "/",
    icon: "grid_view",
    profiles: [...USERS, "vic-qa-no-access", "vic-prod-no-access"],
  },
  {
    title: "Core Data",
    icon: "database",
    children: [
      {
        title: "Materials",
        url: "/materials",
        icon: "inventory",
        profiles: USERS,
      },
      {
        title: "Placeholder Materials",
        url: "/placeholdermaterials",
        icon: "data_object",
        profiles: USERS,
      },
      { title: "Systems", url: "/systems", icon: "wysiwyg", profiles: USERS },
      {
        title: "System Applications",
        url: "/systemapplications",
        icon: "device_hub",
        profiles: USERS,
      },
      { title: "Licenses", url: "/licenses", icon: "badge", profiles: USERS },
      { title: "Contracts", url: "/contracts", icon: "gavel", profiles: USERS },
      { title: "Orders", url: "/orders", icon: "storage", profiles: USERS },
      {
        title: "Serial Numbers",
        url: "/serialnumbers",
        icon: "data_array",
        profiles: USERS,
      },
    ],
  },
  {
    title: "Hardware & SNs",
    icon: "memory",
    children: [
      {
        title: "Hardware Support Process",
        url: "/hwsprocess",
        icon: "device_unknown",
        profiles: USERS,
      },
      {
        title: "Missing SNs Status",
        url: "/missingsnsstatus",
        icon: "rule",
        profiles: USERS,
      },
      {
        title: "Data Quality Sap Orders",
        url: "/dataqualitysaporders",
        icon: "document_scanner",
        profiles: USERS,
      },
    ],
  },
  {
    title: "Reports & Uploads",
    icon: "folder_open",
    children: [
      {
        title: "Bulk Report",
        url: "/bulkreport",
        icon: "summarize",
        profiles: USERS,
      },
      {
        title: "Upload Document",
        url: "/upload",
        icon: "upload_file",
        profiles: ["vic-qa-admin", "vic-qa-user", "vic-prod-admin"],
      },
    ],
  },
  {
    title: "Data Tools",
    icon: "query_stats",
    children: [
      {
        title: "Athena Query",
        url: "/athena",
        icon: "manage_search",
        profiles: [...ADMINS, "vic-qa-dto", "vic-prod-dto"],
      },
      {
        title: "My Portal",
        url: "/myportal",
        icon: "web",
        profiles: ["vic-qa-my-portal", "vic-prod-my-portal"],
      },
    ],
  },
  {
    title: "Supervision",
    icon: "monitor_heart",
    children: [
      {
        title: "Monitoring",
        url: "/monitoring",
        icon: "bar_chart",
        profiles: ADMINS,
      },
      {
        title: "SFDC Synchronizer Monitoring",
        url: "/sfdcsynchronizermonitoring",
        icon: "sync",
        profiles: USERS,
      },
      {
        title: "Unprocessed Items",
        url: "/unprocesseditems",
        icon: "find_in_page",
        profiles: ADMINS,
      },
      {
        title: "Client Errors",
        url: "/clienterrors",
        icon: "report_problem",
        profiles: ADMINS,
      },
    ],
  },
];

// Flat list of every page, for lookups by url.
export const MENU_ITEMS = MENU_SECTIONS.flatMap(
  (entry) => entry.children ?? [entry],
);

const isAllowed = (item, profiles) =>
  item.profiles.some((p) => profiles.includes(p));

// Sections and top-level entries the user may see: children are filtered by
// profile and a section left empty is dropped.
export function visibleSections(profiles) {
  return MENU_SECTIONS.map((entry) =>
    entry.children
      ? {
          ...entry,
          children: entry.children.filter((c) => isAllowed(c, profiles)),
        }
      : entry,
  ).filter((entry) =>
    entry.children ? entry.children.length > 0 : isAllowed(entry, profiles),
  );
}

// a section stays active on its detail pages
const isSelected = (url, pathname) =>
  url === "/"
    ? pathname === "/"
    : Boolean(matchPath({ path: url, end: false }, pathname));

const OPEN_SECTIONS_KEY = "victoria_menu_sections";

function readOpenSections() {
  try {
    return JSON.parse(localStorage.getItem(OPEN_SECTIONS_KEY)) || {};
  } catch (e) {
    return {};
  }
}

function writeOpenSections(value) {
  try {
    localStorage.setItem(OPEN_SECTIONS_KEY, JSON.stringify(value));
  } catch (e) {
    // storage unavailable: the state just won't survive a reload
  }
}

function MenuLink({ item, open, nested, pathname, onClick }) {
  const selected = isSelected(item.url, pathname);
  return (
    <Tooltip title={open ? "" : item.title} placement="right">
      <ListItemButton
        selected={selected}
        aria-current={selected ? "page" : undefined}
        aria-label={open ? undefined : item.title}
        onClick={onClick}
        sx={{
          minHeight: 40,
          mb: 0.5,
          pl: open ? (nested ? 4.5 : 1.5) : 0,
          pr: open ? 1.5 : 0,
          justifyContent: open ? "flex-start" : "center",
          color: "grey.700",
        }}
      >
        <ListItemIcon
          sx={{ minWidth: open ? 36 : 0, justifyContent: "center" }}
        >
          <MaterialSymbol name={item.icon} size="sidebar" filled={selected} />
        </ListItemIcon>
        {open && (
          <ListItemText
            primary={item.title}
            primaryTypographyProps={{
              variant: "body2",
              fontWeight: selected ? 600 : 500,
              noWrap: true,
            }}
          />
        )}
      </ListItemButton>
    </Tooltip>
  );
}

function MenuSection({ section, open, expanded, onToggle, pathname, go }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const active = section.children.some((c) => isSelected(c.url, pathname));

  const header = (
    <Tooltip title={open ? "" : section.title} placement="right">
      <ListItemButton
        aria-expanded={open ? expanded : Boolean(anchorEl)}
        aria-haspopup={open ? undefined : "menu"}
        aria-label={open ? undefined : section.title}
        onClick={(event) =>
          open ? onToggle() : setAnchorEl(event.currentTarget)
        }
        sx={{
          minHeight: 40,
          mb: 0.5,
          px: open ? 1.5 : 0,
          justifyContent: open ? "flex-start" : "center",
          color: "grey.700",
        }}
      >
        <ListItemIcon
          sx={{ minWidth: open ? 36 : 0, justifyContent: "center" }}
        >
          <MaterialSymbol name={section.icon} size="sidebar" filled={active} />
        </ListItemIcon>
        {open && (
          <>
            <ListItemText
              primary={section.title}
              primaryTypographyProps={{
                variant: "body2",
                fontWeight: active ? 600 : 500,
                noWrap: true,
              }}
            />
            <MaterialSymbol
              name={expanded ? "expand_less" : "expand_more"}
              size="sidebar"
            />
          </>
        )}
      </ListItemButton>
    </Tooltip>
  );

  if (!open) {
    // Collapsed sidebar has no room for nested labels: the section icon
    // opens a popover listing its pages instead.
    return (
      <>
        {header}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          {section.children.map((item) => {
            const selected = isSelected(item.url, pathname);
            return (
              <MenuItem
                key={item.url}
                selected={selected}
                onClick={() => {
                  setAnchorEl(null);
                  go(item.url);
                }}
              >
                <ListItemIcon>
                  <MaterialSymbol
                    name={item.icon}
                    size="sidebar"
                    filled={selected}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={item.title}
                  primaryTypographyProps={{ variant: "body2" }}
                />
              </MenuItem>
            );
          })}
        </Menu>
      </>
    );
  }

  return (
    <>
      {header}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {section.children.map((item) => (
            <MenuLink
              key={item.url}
              item={item}
              open={open}
              nested
              pathname={pathname}
              onClick={() => go(item.url)}
            />
          ))}
        </List>
      </Collapse>
    </>
  );
}

export default function MainListItems({ open = true, onNavigate }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [openSections, setOpenSections] = React.useState(readOpenSections);
  let profiles = getUserProfiles();
  if (!profiles) {
    // No matching item found
    return <Navigate to="/fallback" replace />;
  }

  const go = (url) => {
    navigate(url);
    onNavigate && onNavigate();
  };

  const toggleSection = (title, expanded) => {
    const next = { ...openSections, [title]: !expanded };
    setOpenSections(next);
    writeOpenSections(next);
  };

  return (
    <React.Fragment>
      {visibleSections(profiles).map((entry) => {
        // a single visible page is not worth a folder
        const item =
          entry.children && entry.children.length === 1
            ? entry.children[0]
            : entry;
        if (!item.children) {
          return (
            <MenuLink
              key={item.url}
              item={item}
              open={open}
              pathname={pathname}
              onClick={() => go(item.url)}
            />
          );
        }
        // the section holding the current page opens unless explicitly closed
        const active = item.children.some((c) => isSelected(c.url, pathname));
        const expanded = openSections[item.title] ?? active;
        return (
          <MenuSection
            key={item.title}
            section={item}
            open={open}
            expanded={expanded}
            onToggle={() => toggleSection(item.title, expanded)}
            pathname={pathname}
            go={go}
          />
        );
      })}
    </React.Fragment>
  );
}
